import './View.styles.css';
import { useNavigate } from 'react-router-dom';
import { viewRegulation } from '../../Regulation.services';
import { useRegulation } from '../../useRegulation';

import { HomeOutlined } from '@ant-design/icons';
import { FileUploadView } from '../../../../components/file-upload-view';
import { FiAlertCircle, FiAlertTriangle, FiPrinter } from 'react-icons/fi';
import { Breadcrumb } from 'antd';
import { useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../../hooks/usePermissions.ts';
import dayjs from 'dayjs';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../../../constants/app.ts';
import { getParentTopic } from '../../../../helpers';
import { useService as useTopicsService } from '../../../topics/Topics.hooks.ts';
import { BannerExpired } from '../banner-expired';
import { BannerDeleted } from '../banner-deleted';
import { BannerDeletedAdmin } from '../banner-deleted-admin';
import { BannerUnpublished } from '../banner-unpublished';
import { useAuth } from '../../../../context/AuthContext.tsx';

export default function Content() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { topics } = useTopicsService({ topicId: null });
  const { id, isHistory, data } = useRegulation();
  const { isAdmin } = usePermissions();
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  const isInLeadership = data?.unitInCharge?.leadership?.includes(user?.id);

  useEffect(() => {
    if (!data?.id || String(data.id) !== id) return;
    viewRegulation(String(data.id));
  }, [data?.id, id]);

  useEffect(() => {
    document.title = data?.title || 'GI Arbeitsgrundlagen';
  }, [data?.title]);

  return (
    <>
      <div className="breadcrumbs-container">
        <Breadcrumb
          className="breadcrumb"
          items={[
            {
              href: '/',
              title: <HomeOutlined />,
            },
            {
              href: '/',
              title: t('common.portal'),
            },
          ]}
        />
        <div onClick={reactToPrintFn} className="print-button">
          {t('common.print')} <FiPrinter />
        </div>
      </div>
      <div className="content-container">
        <div className="draft-page print-view" ref={contentRef}>
          <div className="draft-title">{data?.title}</div>
          <div className="print-extra-info" style={{ display: 'none' }}>
            <div className="extra-info">
              <div className="extra-info-row">
                <span className="extra-info-title">{t('pages.raw-regulations.creation')}</span>
                <span>
                  {data?.creationDate ? dayjs(data?.creationDate).format(DATE_FORMAT) : '-'}
                </span>
              </div>
              <div className="extra-info-row">
                <span className="extra-info-title">{t('pages.raw-regulations.publication')}</span>
                <span>
                  {data?.publicationDate ? dayjs(data?.publicationDate).format(DATE_FORMAT) : '-'}
                </span>
              </div>
              <div className="extra-info-row">
                <span className="extra-info-title">{t('pages.raw-regulations.deletion')}</span>
                <span>
                  {data?.deletionDate ? dayjs(data?.deletionDate).format(DATE_FORMAT) : '-'}
                </span>
              </div>
              <div className="extra-info-row">
                <span className="extra-info-title">{t('pages.raw-regulations.expiration')}</span>
                <span>
                  {data?.confirmationDate ? dayjs(data?.confirmationDate).format(DATE_FORMAT) : '-'}
                </span>
              </div>
              <div className="extra-info-row">
                <span className="extra-info-title">{t('pages.raw-regulations.attachments')}</span>
                <span>{data?.attachments?.length || '-'}</span>
              </div>
            </div>
          </div>
          {isHistory && !data?.isActive && (
            <div className="banner-alert banner-alert--warning" style={{ marginTop: 16, marginBottom: 16 }}>
              <span>
                <FiAlertTriangle fontSize={20} color="#FF4D4F" />
              </span>
              <div className="banner-alert-content">
                <span className="banner-label">
                  {t('common.version-from')} {dayjs(data?.publicationDate).format(DATE_TIME_FORMAT)}
                </span>
              </div>
            </div>
          )}
          {data?.isDeleted && isAdmin && (
            <BannerDeletedAdmin
              time={data?.deletionDate ? dayjs(data?.deletionDate).format(DATE_TIME_FORMAT) : ''}
            />
          )}

          {data?.isDeleted && !isAdmin && (
            <BannerDeleted title={data?.title} unit={data?.unitInCharge?.title} />
          )}

          {data?.expirationDate && dayjs(data?.expirationDate).isBefore(new Date()) && (
            <BannerExpired />
          )}
          {((!data?.isDeleted && !isAdmin) ||
            (data?.isDeleted && isAdmin) ||
            (!data?.isDeleted && isAdmin)) && (
            <>
              <div className="regulation-status-container" style={{ marginTop: 20 }}>
                <div className="regulation-status-icon">
                  <FiAlertCircle fontSize={20} color="var(--primary-color)" />
                </div>
                <div className="regulation-status">
                  <div className="regulation-status-label">{t('common.alert-status')}</div>
                  {data?.topics?.map((topic: { id: string }) => {
                    const node = topics?.find((tc: { id: string }) => tc.id === topic.id);
                    const nodes = getParentTopic(node, [], topics);
                    const parentsNames = nodes
                      .reverse()
                      .map((n: { name: string }) => n.name)
                      .join(' / ');
                    const parent = `${parentsNames}${parentsNames ? ' / ' : ''}`;
                    return (
                      <div>
                        <span>
                          {parent}
                          <span
                            className="regulation-status-value"
                            onClick={() => navigate(`/?topicId=${node.id}`)}
                          >
                            {node?.name}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="draft-en-summary">
                <span className="draft-label">{t('form.field.summary')}</span>
                <span dangerouslySetInnerHTML={{ __html: data?.shortDescription }} />
              </div>
              <div className="draft-en-summary">
                <span className="draft-label">{t('form.field.summary-english')}</span>
                <span dangerouslySetInnerHTML={{ __html: data?.shortDescriptionEnglish }} />
              </div>
              <div className="draft-en-summary">
                <span className="draft-label">{t('form.field.content')}</span>
                <span dangerouslySetInnerHTML={{ __html: data?.content }} />
              </div>
              <div className="draft-links">
                <span className="draft-label">{t('form.field.links')}</span>
                {data?.links?.map((link: { title: string; url: string }) => (
                  <a key={link.url} href={link.url} target="_blank">
                    <span>{link.title}</span>
                  </a>
                ))}
              </div>
              <div className="draft-files">
                <span className="draft-label">{t('form.field.attachments')}</span>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {data?.attachments?.map((attachment: any) => (
                  <a key={attachment.sharePointItemId} href={attachment.url} target="_blank">
                    <FileUploadView file={attachment} />
                  </a>
                ))}
              </div>
            </>
          )}

          {data && (
            <BannerUnpublished
              id={data.id}
              showAttention={data?.showAttention}
              draftId={data.regulationDraftId}
              isInLeadership={isInLeadership}
              title={data.title}
              forwardEmails={data.forwardEmails}
              isCompanyAgreement={data.type === 2}
              isValid={data?.isValid}
              isReminderSent={data?.isReminderSent}
            />
          )}
        </div>
      </div>
    </>
  );
}
